import {
  FirebaseApp,
  deleteApp,
  getApps,
  initializeApp,
} from 'firebase/app'
import {
  ReCaptchaV3Provider,
  initializeAppCheck,
} from 'firebase/app-check'
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  getFirestore,
} from 'firebase/firestore'
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
} from 'firebase/functions'
import {
  connectStorageEmulator,
  getStorage,
} from 'firebase/storage'
import {
  firebaseConfig,
  useFirebaseEmulators,
} from '../AAinitFirebase'
import useStore, { resetStore } from '../store/useStore'
import {
  clearEffectiveServices,
  primaryServices,
} from './firebaseServiceContext'
import { clearPatientQueryCache } from './queryClient'

export const IMPERSONATION_APP_NAME = 'impersonation'
export const IMPERSONATION_SESSION_KEY = 'als.impersonation.sessionId'
const CHANNEL_NAME = 'als.impersonation'

interface StartResponse {
  token: string
  sessionId: string
  expiresAt: number
}

interface StopResponse {
  success: boolean
  alreadyStopped?: boolean
}

const notifyTabs = (type: 'started' | 'stopped', sessionId?: string) => {
  if (typeof BroadcastChannel === 'undefined') return
  const channel = new BroadcastChannel(CHANNEL_NAME)
  channel.postMessage({ type, sessionId })
  channel.close()
}

export const getStoredImpersonationSessionId = (): string | null =>
  localStorage.getItem(IMPERSONATION_SESSION_KEY)

export const getOrCreateImpersonationApp = (): FirebaseApp => {
  const existing = getApps().find(
    (candidate) => candidate.name === IMPERSONATION_APP_NAME,
  )
  if (existing) return existing

  const app = initializeApp(firebaseConfig, IMPERSONATION_APP_NAME)
  if (process.env.NODE_ENV === 'production') {
    const siteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY
    if (!siteKey) {
      throw new Error(
        'Secondary impersonation services require Firebase App Check.',
      )
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    })
  }
  if (useFirebaseEmulators) {
    connectAuthEmulator(getAuth(app), 'http://127.0.0.1:9099', {
      disableWarnings: true,
    })
    connectFirestoreEmulator(getFirestore(app), '127.0.0.1', 8080)
    connectFunctionsEmulator(getFunctions(app), '127.0.0.1', 5001)
    connectStorageEmulator(getStorage(app), '127.0.0.1', 9199)
  }
  return app
}

export const getImpersonationAuth = () => {
  const app = getApps().find(
    (candidate) => candidate.name === IMPERSONATION_APP_NAME,
  )
  return app ? getAuth(app) : null
}

export const destroyImpersonationApp = async (): Promise<void> => {
  const app = getApps().find(
    (candidate) => candidate.name === IMPERSONATION_APP_NAME,
  )
  if (!app) return
  try {
    await signOut(getAuth(app))
  } finally {
    await deleteApp(app)
  }
}

export const clearLocalImpersonation = async (): Promise<void> => {
  clearEffectiveServices()
  resetStore()
  await clearPatientQueryCache()
  localStorage.removeItem(IMPERSONATION_SESSION_KEY)
  await destroyImpersonationApp().catch(() => undefined)
  const actor = useStore.getState().actorIdentity
  useStore.setState({
    impersonationSession: undefined,
    effectiveIdentity: actor
      ? { uid: actor.uid, source: 'primary' }
      : undefined,
    identityStatus: actor ? 'primary' : 'initializing',
  })
}

export const destroyZombieSession = (): void => {
  void clearLocalImpersonation()
}

export const startImpersonation = async (
  targetUid: string,
  reason: string,
): Promise<void> => {
  useStore.setState({ identityStatus: 'verifying' })
  await clearPatientQueryCache()
  resetStore()
  let createdSessionId: string | undefined

  try {
    const callable = httpsCallable<
      { targetUid: string; reason: string },
      StartResponse
    >(primaryServices.functions, 'impersonateUserCallable')
    const { data } = await callable({ targetUid, reason })
    createdSessionId = data.sessionId
    const app = getOrCreateImpersonationApp()
    const secondaryAuth = getAuth(app)
    await setPersistence(secondaryAuth, browserLocalPersistence)
    const credential = await signInWithCustomToken(secondaryAuth, data.token)
    if (credential.user.uid !== targetUid) {
      throw new Error('Authenticated target does not match the requested user.')
    }
    // Publish the opaque server session as soon as secondary Auth has switched.
    // This lets other tabs reconcile the new shared Auth state before they
    // attempt to validate it against the superseded session.
    localStorage.setItem(IMPERSONATION_SESSION_KEY, data.sessionId)
    const claims = (await credential.user.getIdTokenResult()).claims
    if (
      claims.impersonationSessionId !== data.sessionId ||
      claims.impersonatorUid !== primaryServices.auth.currentUser?.uid
    ) {
      throw new Error('Impersonation token binding is invalid.')
    }
    notifyTabs('started', data.sessionId)
    window.location.assign('/')
  } catch (error) {
    if (createdSessionId) {
      await revokeWithPrimaryIdentity(createdSessionId, 'error').catch(
        () => undefined,
      )
    }
    await clearLocalImpersonation()
    throw error
  }
}

const revokeWithPrimaryIdentity = async (
  sessionId: string,
  reason: 'logout' | 'error',
): Promise<void> => {
  const callable = httpsCallable<
    { sessionId: string; reason: 'logout' | 'error' },
    { success: boolean }
  >(primaryServices.functions, 'revokeImpersonationSessionCallable')
  await callable({ sessionId, reason })
}

export const stopImpersonation = async (
  reason: 'explicit' | 'timeout' | 'logout' | 'error' = 'explicit',
  navigate = true,
): Promise<void> => {
  const sessionId =
    useStore.getState().impersonationSession?.sessionId ??
    getStoredImpersonationSessionId()
  if (!sessionId) {
    await clearLocalImpersonation()
    return
  }

  useStore.setState({ identityStatus: 'invalidating' })
  let stopError: unknown
  const app = getApps().find(
    (candidate) => candidate.name === IMPERSONATION_APP_NAME,
  )
  try {
    if (!app) throw new Error('Secondary Firebase app is unavailable.')
    const callable = httpsCallable<
      { sessionId: string; reason: typeof reason },
      StopResponse
    >(getFunctions(app), 'stopImpersonationCallable')
    await callable({ sessionId, reason })
  } catch (error) {
    stopError = error
    try {
      await revokeWithPrimaryIdentity(
        sessionId,
        reason === 'logout' ? 'logout' : 'error',
      )
      stopError = undefined
    } catch {
      // Local access is still destroyed. The caller receives the original
      // failure so the UI can visibly report that server revocation is pending.
    }
  } finally {
    notifyTabs('stopped', sessionId)
    await clearLocalImpersonation()
  }

  if (stopError) {
    window.alert(
      'The local session was closed, but server revocation could not be confirmed. Please contact support before continuing.',
    )
  }
  if (navigate) window.location.assign('/admin/users')
  if (stopError) throw stopError
}

if (getStoredImpersonationSessionId()) {
  getOrCreateImpersonationApp()
}
